import { trpc } from '@/utils/trpc';
import { DomainTableData } from '../types';
import { notificationService } from '@shared/components/notifications';

/**
 * Helper function to check if error should be retried
 * Don't retry on 4xx client errors, but retry on server errors
 */
function shouldRetryError(failureCount: number, error: unknown): boolean {
  // Type-safe check for httpStatus property
  const httpStatus =
    error && typeof error === 'object' && 'data' in error
      ? (error.data as { httpStatus?: number } | null | undefined)?.httpStatus
      : undefined;

  // Don't retry on 4xx errors
  if (httpStatus && httpStatus >= 400 && httpStatus < 500) {
    return false;
  }

  // Retry up to 3 times for other errors
  return failureCount < 3;
}

/**
 * Query hook for fetching domains by tenant with cursor-based pagination
 * Uses tRPC for type-safe API calls
 */
export const useDomains = (tenantId: string) => {
  return trpc.domain.list.useInfiniteQuery(
    { tenantId, limit: 20 },
    {
      getNextPageParam: (lastPage) => lastPage.cursor,
      enabled: !!tenantId,
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: shouldRetryError,
    }
  );
};

/**
 * Query hook for fetching a single domain by ID
 * Uses tRPC for type-safe API calls
 *
 * @param tenantId - UUID of the parent tenant (required for DynamoDB key lookup)
 * @param domainId - UUID of the domain
 */
export const useDomain = (tenantId: string, domainId: string) => {
  return trpc.domain.getById.useQuery(
    { tenantId, domainId },
    {
      enabled: !!tenantId && !!domainId,
      staleTime: 5 * 60 * 1000,
      retry: shouldRetryError,
    }
  );
};

/**
 * Mutation hook for creating a domain
 * Uses tRPC for type-safe API calls with automatic cache invalidation
 *
 * Maintains backward compatibility by accepting data without tenantId
 * and adding it automatically based on the hook parameter
 */
export const useCreateDomain = (tenantId: string) => {
  const utils = trpc.useUtils();

  const mutation = trpc.domain.create.useMutation({
    onSuccess: (newDomain) => {
      // Invalidate the list query for this tenant to refetch
      utils.domain.list.invalidate({ tenantId });

      if (newDomain) {
        notificationService.success(`Institute "${newDomain.name}" created successfully`);
      }
    },
    onError: (error) => {
      const errorMessage = error?.message || 'Failed to create institute';
      notificationService.error(errorMessage);
    },
  });

  // Wrapper to maintain backward compatibility with existing component interface
  // Components call: mutate({ name, description?, code? })
  // tRPC expects: mutate({ tenantId, name, description?, code? })
  return {
    ...mutation,
    mutate: (variables: { name: string; description?: string; code?: string }) => {
      mutation.mutate({
        tenantId,
        ...variables,
      });
    },
    mutateAsync: async (variables: { name: string; description?: string; code?: string }) => {
      return mutation.mutateAsync({
        tenantId,
        ...variables,
      });
    },
  };
};

/**
 * Mutation hook for updating a domain
 * Uses tRPC for type-safe API calls with automatic cache invalidation
 *
 * Maintains backward compatibility by transforming the component's interface
 * from { domainId, data: {...} } to tRPC's { tenantId, id, ...data }
 */
export const useUpdateDomain = (tenantId: string) => {
  const utils = trpc.useUtils();

  const mutation = trpc.domain.update.useMutation({
    onSuccess: (updatedDomain) => {
      if (updatedDomain) {
        // Invalidate the specific domain and list queries
        utils.domain.getById.invalidate({
          tenantId: updatedDomain.tenantId,
          domainId: updatedDomain.domainId,
        });
        utils.domain.list.invalidate({ tenantId });

        notificationService.success(`Institute "${updatedDomain.name}" updated successfully`);
      }
    },
    onError: (error) => {
      const errorMessage = error?.message || 'Failed to update institute';
      notificationService.error(errorMessage);
    },
  });

  // Wrapper to maintain backward compatibility with existing component interface
  // Components call: mutate({ domainId, data: { name?, description?, code? } })
  // tRPC expects: mutate({ tenantId, id, name?, description?, code? })
  return {
    ...mutation,
    mutate: (variables: {
      domainId: string;
      data: { name?: string; description?: string; code?: string };
    }) => {
      mutation.mutate({
        tenantId,
        id: variables.domainId,
        ...variables.data,
      });
    },
    mutateAsync: async (variables: {
      domainId: string;
      data: { name?: string; description?: string; code?: string };
    }) => {
      return mutation.mutateAsync({
        tenantId,
        id: variables.domainId,
        ...variables.data,
      });
    },
  };
};

/**
 * Mutation hook for deleting a domain
 * Uses tRPC for type-safe API calls with automatic cache invalidation
 *
 * Maintains backward compatibility by accepting a string domainId
 * and transforming it to the tRPC format { tenantId, id: domainId }
 */
export const useDeleteDomain = (tenantId: string) => {
  const utils = trpc.useUtils();

  const mutation = trpc.domain.delete.useMutation({
    onSuccess: (_result, variables) => {
      // Invalidate cache queries
      utils.domain.getById.invalidate({
        tenantId: variables.tenantId,
        domainId: variables.id,
      });
      utils.domain.list.invalidate({ tenantId });

      notificationService.success('Institute deleted successfully');
    },
    onError: (error) => {
      const errorMessage = error?.message || 'Failed to delete institute';
      notificationService.error(errorMessage);
    },
  });

  // Wrapper to maintain backward compatibility with existing component interface
  // Components pass: domainId (string)
  // tRPC expects: { tenantId, id: domainId }
  return {
    ...mutation,
    mutate: (domainId: string) => {
      mutation.mutate({ tenantId, id: domainId });
    },
    mutateAsync: async (domainId: string) => {
      return mutation.mutateAsync({ tenantId, id: domainId });
    },
  };
};

/**
 * Computed hook for transforming domain data for table display
 * Flattens the paginated structure for components expecting a simple array
 */
export const useDomainsTableData = (tenantId: string) => {
  const query = trpc.domain.list.useQuery(
    { tenantId, limit: 100 },
    {
      enabled: !!tenantId,
      staleTime: 5 * 60 * 1000,
      retry: shouldRetryError,
    }
  );

  // Transform tRPC response to table data format
  const tableData: DomainTableData[] = (query.data?.items || []).map((domain) => ({
    id: domain.domainId,
    tenantId: domain.tenantId,
    name: domain.name,
    description: domain.description,
    code: domain.code,
    createdAt: domain.createdAt,
  }));

  return {
    data: tableData,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
};
